/**
 * Settings-Screen — Character customization + Theme + Notifications + Shortcuts
 * Two-column layout: settings left, 3D character preview right.
 * Mock reference: corpllm-assets/gallery/settings-mock.html
 */
import { clearJwt, getJwt, parseJwt } from '../auth';
import { getPreferences, patchPreferences } from '../api';
import type { Preferences } from '../api';
import { t, setLang, getLang } from '../i18n';
import { resolveAsset } from '../data/AssetResolver';
import {
  CharacterPreview,
  CHARACTERS, MST_TONES,
  HAIR, HEADGEAR, EARRINGS, PIERCINGS, MASKS,
  type AttachmentDef,
} from './character-preview';

const ACCENT_PRESETS: { name: string; color: string }[] = [
  { name: 'Cyan',    color: '#00f5d4' },
  { name: 'Magenta', color: '#f72585' },
  { name: 'Amber',   color: '#fca311' },
  { name: 'Green',   color: '#06d6a0' },
  { name: 'Purple',  color: '#9b5de5' },
  { name: 'Red',     color: '#e63946' },
  { name: 'Blue',    color: '#4361ee' },
  { name: 'Pink',    color: '#ff6b9d' },
  { name: 'Lime',    color: '#a7f542' },
  { name: 'Orange',  color: '#ff8c42' },
];

function getShortcuts(): { key: string; action: string }[] {
  return [
    { key: '1–9',      action: t('settings.shortcut_switch_screen') },
    { key: 'N',        action: t('settings.shortcut_notifications') },
    { key: 'Esc',      action: t('settings.shortcut_close_panel') },
    { key: '?',        action: t('settings.shortcut_show_shortcuts') },
    { key: '↑/↓',     action: t('settings.shortcut_plan_focus') },
    { key: 'Alt+↑/↓', action: t('settings.shortcut_plan_reorder') },
  ];
}

function getDefaultCorpName(): string {
  try { return localStorage.getItem('defaultCorpName') ?? ''; } catch { return ''; }
}

export function renderSettings(): HTMLElement {
  const root = document.createElement('div');
  root.className = 'screen';

  const style = document.createElement('style');
  style.textContent = `
    .set-content { display: flex; max-width: 1200px; padding: 20px 0; gap: 40px; }
    .set-col { flex: 1; max-width: 420px; }
    .set-preview-col { flex: 1; display: flex; flex-direction: column; align-items: center; }

    .set-section { margin-bottom: 28px; }
    .set-section-title { font-size: 11px; color: var(--text-dim); text-transform: uppercase;
      letter-spacing: 0.15em; margin-bottom: 10px; padding-bottom: 4px;
      border-bottom: 1px solid rgba(255,255,255,0.06); font-family: var(--mono); }
    .set-row { display: flex; align-items: center; gap: 12px; padding: 8px 0; font-size: 0.85rem; }
    .set-label { color: var(--text-mid); width: 160px; flex-shrink: 0; font-size: 0.78rem; }
    .set-value { color: var(--cyan); font-family: var(--mono); font-size: 0.85rem; }
    /* .set-accent-btn → shared .swatch (marker kept for tests) */
    .set-toggle { position: relative; width: 36px; height: 20px; background: rgba(255,255,255,0.1);
      border-radius: 10px; cursor: pointer; border: none; transition: background 0.2s; flex-shrink: 0; }
    .set-toggle.on { background: rgba(var(--ac),0.3); }
    .set-toggle::after { content: ''; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px;
      border-radius: 50%; background: var(--text-mid); transition: transform 0.2s; }
    .set-toggle.on::after { transform: translateX(16px); background: var(--cyan); }
    .set-shortcut-row { display: flex; gap: 12px; padding: 5px 0; font-size: 0.8rem; }
    .set-shortcut-key { color: var(--cyan); font-family: var(--mono); width: 60px;
      flex-shrink: 0; font-size: 0.78rem; }
    .set-shortcut-desc { color: var(--text-mid); }
    /* .set-logout → shared .btn.red (marker kept) */
    .set-save-status { font-size: 0.72rem; font-family: var(--mono); margin-left: 8px; transition: opacity 0.3s; }
    /* .set-input → shared .field (marker kept; +flex/max-width inline) */
    .set-input { flex: 1; max-width: 260px; color: var(--cyan); }
    .set-hint { font-size: 0.68rem; color: var(--text-lo); margin-top: 2px; }

    /* Character grid */
    .set-char-grid { display: flex; flex-wrap: wrap; gap: 6px; }
    .set-char-btn { width: 64px; height: 64px; border: 2px solid var(--border); border-radius: 6px;
      background: var(--bg-deep); cursor: pointer; transition: all 0.2s;
      position: relative; overflow: hidden; padding: 0;
      background-size: cover; background-position: center top; }
    .set-char-btn:hover { border-color: var(--border-hi); }
    .set-char-btn.active { border-color: var(--cyan); box-shadow: 0 0 12px rgba(var(--ac),0.15); }

    /* MST swatches */
    .set-mst-grid { display: flex; gap: 4px; }
    .set-mst-btn { width: 28px; height: 28px; border-radius: 4px; border: 2px solid transparent;
      cursor: pointer; transition: border-color 0.15s; }
    .set-mst-btn:hover, .set-mst-btn.active { border-color: #fff; }

    /* Attachment chips */
    .set-attach-grid { display: flex; flex-wrap: wrap; gap: 4px; }
    /* .set-attach-chip → shared .chip (marker kept); only the active state stays local */
    .set-attach-chip.active { border-color: var(--cyan); color: var(--cyan); background: rgba(var(--ac),0.06); }

    /* 3D Preview */
    .set-preview-canvas { width: 500px; height: 620px; background: var(--bg-deep);
      border: 1px solid var(--border); border-radius: 8px; }
    .set-preview-controls { display: flex; gap: 8px; margin-top: 10px; }
    /* .set-cam-btn → shared .btn.sm (marker kept) */
    .set-preview-hint { font-size: 9px; color: var(--text-lo); font-family: var(--mono);
      margin-top: 6px; opacity: 0.6; }

    @media (max-width: 900px) {
      .set-content { flex-direction: column-reverse; align-items: center; }
      .set-col { max-width: 100%; }
      .set-preview-canvas { width: 340px; height: 420px; }
    }
  `;
  root.appendChild(style);

  // ── Two-column layout ──
  const content = document.createElement('div');
  content.className = 'set-content';

  const settingsCol = document.createElement('div');
  settingsCol.className = 'set-col';

  const previewCol = document.createElement('div');
  previewCol.className = 'set-preview-col';

  // Mutable local state — synced from/to server
  const filters = { crit: true, warn: true, info: true };

  // ── Settings Column ──
  settingsCol.innerHTML = `
    <div class="set-section">
      <div class="set-section-title">${t('settings.lobby_defaults')}</div>
      <div class="set-row" style="flex-direction:column; align-items:flex-start; gap:4px;">
        <span class="set-label">${t('settings.default_corp_name')}</span>
        <input class="set-input field" id="set-default-corp" type="text" maxlength="40"
          aria-label="${t('settings.default_corp_name')}"
          value="${escHtml(getDefaultCorpName())}" autocomplete="off" />
        <span class="set-hint">${t('settings.default_corp_hint')}</span>
      </div>
    </div>

    <div class="set-section">
      <div class="set-section-title">${t('settings.theme')} <span class="set-save-status" id="set-accent-status"></span></div>
      <div class="set-row" id="set-accent-row">
        ${ACCENT_PRESETS.map((p, i) =>
          `<button class="set-accent-btn swatch${i === 0 ? ' active' : ''}" data-color="${p.color}" title="${p.name}" style="background:${p.color}"></button>`
        ).join('')}
      </div>
      <div class="set-row" style="margin-top:8px">
        <span class="set-label">Font Size</span>
        <select class="set-input field" id="set-font-size" aria-label="Font Size" style="width:120px; background:var(--bg); border:1px solid var(--border); color:var(--text-hi); padding:4px 8px; font-family:var(--mono); font-size:12px;">
          <option value="12">Small (12px)</option>
          <option value="14" selected>Normal (14px)</option>
          <option value="16">Large (16px)</option>
          <option value="18">XL (18px)</option>
        </select>
      </div>
    </div>

    <div class="set-section">
      <div class="set-section-title">Character</div>
      <div class="set-char-grid" id="set-char-grid"></div>
    </div>

    <div class="set-section">
      <div class="set-section-title">Skin Tone</div>
      <div class="set-row">
        <div class="set-mst-grid" id="set-mst-grid"></div>
      </div>
    </div>

    <div class="set-section">
      <div class="set-section-title">Pose</div>
      <div class="set-row" style="gap:4px;">
        <button class="set-attach-chip chip active" id="set-pose-t">T-Pose</button>
        <button class="set-attach-chip chip" id="set-pose-a">A-Pose</button>
        <button class="set-attach-chip chip" id="set-pose-i">I-Pose</button>
      </div>
    </div>

    <div class="set-section">
      <div class="set-section-title">Accessories</div>
      <div class="set-row" style="flex-direction:column;align-items:flex-start;gap:6px;">
        <span class="set-label" style="width:auto;">Hair</span>
        <div class="set-attach-grid" id="set-hair-grid"></div>
      </div>
      <div class="set-row" style="flex-direction:column;align-items:flex-start;gap:6px;">
        <span class="set-label" style="width:auto;">Hats</span>
        <div class="set-attach-grid" id="set-headgear-grid"></div>
      </div>
      <div class="set-row" style="flex-direction:column;align-items:flex-start;gap:6px;">
        <span class="set-label" style="width:auto;">Earrings</span>
        <div class="set-attach-grid" id="set-earring-grid"></div>
      </div>
      <div class="set-row" style="flex-direction:column;align-items:flex-start;gap:6px;">
        <span class="set-label" style="width:auto;">Piercings</span>
        <div class="set-attach-grid" id="set-piercing-grid"></div>
      </div>
      <div class="set-row" style="flex-direction:column;align-items:flex-start;gap:6px;">
        <span class="set-label" style="width:auto;">Mask</span>
        <div class="set-attach-grid" id="set-mask-grid"></div>
      </div>
    </div>

    <div class="set-section">
      <div class="set-section-title">Language</div>
      <div class="set-row" style="gap:4px;">
        <button class="set-attach-chip chip${getLang() === 'de' ? ' active' : ''}" id="set-lang-de">DE</button>
        <button class="set-attach-chip chip${getLang() === 'en' ? ' active' : ''}" id="set-lang-en">EN</button>
      </div>
    </div>

    <div class="set-section">
      <div class="set-section-title">${t('settings.notif_filter')} <span class="set-save-status" id="set-notif-status"></span></div>
      <div class="set-row">
        <span class="set-label">${t('settings.notif_crit')}</span>
        <button class="set-toggle on" id="set-notif-crit" role="switch" aria-checked="true" aria-label="${t('settings.notif_crit')}"></button>
      </div>
      <div class="set-row">
        <span class="set-label">${t('settings.notif_warn')}</span>
        <button class="set-toggle on" id="set-notif-warn" role="switch" aria-checked="true" aria-label="${t('settings.notif_warn')}"></button>
      </div>
      <div class="set-row">
        <span class="set-label">${t('settings.notif_info')}</span>
        <button class="set-toggle on" id="set-notif-info" role="switch" aria-checked="true" aria-label="${t('settings.notif_info')}"></button>
      </div>
    </div>

    ${(() => {
      const jwt = getJwt();
      const payload = jwt ? parseJwt(jwt) : null;
      if (payload?.admin) {
        const isOn = localStorage.getItem('corpllm_admin_mode') === '1';
        return `<div class="set-section">
          <div class="set-section-title">${t('settings.admin')}</div>
          <div class="set-row">
            <span class="set-label">${t('settings.admin_mode')}</span>
            <button class="set-toggle${isOn ? ' on' : ''}" id="set-admin-toggle"></button>
          </div>
          <div class="set-hint" style="margin-top:4px">${t('settings.admin_hint')}</div>
        </div>`;
      }
      return '';
    })()}

    <div class="set-section">
      <div class="set-section-title">${t('settings.shortcuts')}</div>
      ${getShortcuts().map(s =>
        `<div class="set-shortcut-row">
          <span class="set-shortcut-key">${s.key}</span>
          <span class="set-shortcut-desc">${s.action}</span>
        </div>`
      ).join('')}
    </div>

    <div class="set-section">
      <div class="set-section-title">${t('settings.session')}</div>
      <button class="set-logout btn red" id="set-logout">${t('settings.logout_btn')}</button>
    </div>
  `;

  // ── Preview Column ──
  const canvasEl = document.createElement('canvas');
  canvasEl.className = 'set-preview-canvas';
  canvasEl.width = 500;
  canvasEl.height = 620;


  const previewControls = document.createElement('div');
  previewControls.className = 'set-preview-controls';

  const btnFace = document.createElement('button');
  btnFace.className = 'set-cam-btn btn sm';
  btnFace.textContent = `🔍 ${t('settings.btn_face')}`;
  btnFace.title = 'Zoom to face';

  const btnReset = document.createElement('button');
  btnReset.className = 'set-cam-btn btn sm';
  btnReset.textContent = `↺ ${t('settings.btn_reset')}`;
  btnReset.title = 'Reset camera';

  previewControls.appendChild(btnFace);
  previewControls.appendChild(btnReset);

  const previewHint = document.createElement('div');
  previewHint.className = 'set-preview-hint';
  previewHint.textContent = t('settings.hint_3d');

  previewCol.appendChild(canvasEl);
  previewCol.appendChild(previewControls);
  previewCol.appendChild(previewHint);

  content.appendChild(settingsCol);
  content.appendChild(previewCol);
  root.appendChild(content);

  // ── Babylon.js 3D Preview ──
  let preview: CharacterPreview | null = null;
  try {
    preview = new CharacterPreview(canvasEl);
  } catch {
    // WebGL not available — canvas stays empty
  }

  window.addEventListener('resize', () => preview?.resize());

  // Camera control buttons
  btnFace.addEventListener('click', () => preview?.zoomToFace());
  btnReset.addEventListener('click', () => preview?.resetCamera());

  // ── Build character grid ──
  const charGrid = settingsCol.querySelector('#set-char-grid')!;
  CHARACTERS.forEach((ch, i) => {
    const btn = document.createElement('button');
    btn.className = 'set-char-btn' + (i === 0 ? ' active' : '');
    btn.title = ch.name;
    const thumbUrl = resolveAsset(`characters/thumbnails/${ch.file}`);
    btn.style.backgroundImage = `url(${thumbUrl})`;
    btn.addEventListener('click', () => {
      charGrid.querySelectorAll('.set-char-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      // Reset attachment chips to "None"
      settingsCol.querySelectorAll('.set-attach-grid .set-attach-chip').forEach((c) => {
        c.classList.toggle('active', c.textContent === 'None');
      });
      // Reset MST
      settingsCol.querySelectorAll('.set-mst-btn').forEach(b => b.classList.remove('active'));

      preview?.selectCharacter(ch).then(() => saveCharacterState());
    });
    charGrid.appendChild(btn);
  });

  // ── Build MST grid ──
  const mstGrid = settingsCol.querySelector('#set-mst-grid')!;
  MST_TONES.forEach(tone => {
    const btn = document.createElement('button');
    btn.className = 'set-mst-btn';
    btn.style.background = tone.color;
    btn.dataset.color = tone.color;
    btn.title = 'MST ' + tone.level;
    btn.addEventListener('click', () => {
      mstGrid.querySelectorAll('.set-mst-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      preview?.applySkinTone(tone.color);
      saveCharacterState();
    });
    mstGrid.appendChild(btn);
  });

  // ── Pose buttons ──
  for (const id of ['t', 'a', 'i']) {
    const btn = settingsCol.querySelector(`#set-pose-${id}`) as HTMLElement;
    btn.addEventListener('click', () => {
      settingsCol.querySelectorAll('#set-pose-t,#set-pose-a,#set-pose-i').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      preview?.applyPose(id);
      saveCharacterState();
    });
  }

  // ── Build attachment grids ──
  function buildAttachGrid(containerId: string, items: AttachmentDef[], slotName: string): void {
    const grid = settingsCol.querySelector(`#${containerId}`)!;
    items.forEach((item, i) => {
      const chip = document.createElement('button');
      chip.className = 'set-attach-chip chip' + (i === 0 ? ' active' : '');
      chip.textContent = item.name;
      chip.addEventListener('click', () => {
        grid.querySelectorAll('.set-attach-chip').forEach(c => c.classList.remove('active'));
        chip.classList.add('active');
        preview?.equipAttachment(slotName, item).then(() => saveCharacterState());
      });
      grid.appendChild(chip);
    });
  }
  function buildMultiAttachGrid(containerId: string, items: AttachmentDef[], slotName: string): void {
    const grid = settingsCol.querySelector(`#${containerId}`)!;
    items.forEach((item, i) => {
      const chip = document.createElement('button');
      chip.className = 'set-attach-chip chip' + (i === 0 ? ' active' : '');
      chip.textContent = item.name;
      chip.addEventListener('click', () => {
        if (!item.file) {
          // "None" — clear all, reset chips
          grid.querySelectorAll('.set-attach-chip').forEach(c => c.classList.remove('active'));
          chip.classList.add('active');
          preview?.clearMultiSlot(slotName);
          saveCharacterState();
          return;
        }
        // Toggle this chip
        const noneChip = grid.querySelector('.set-attach-chip') as HTMLElement;
        noneChip?.classList.remove('active');
        chip.classList.toggle('active');

        // If nothing active → reactivate "None"
        const anyActive = grid.querySelectorAll('.set-attach-chip.active');
        if (anyActive.length === 0) {
          noneChip?.classList.add('active');
          preview?.clearMultiSlot(slotName);
          saveCharacterState();
        } else {
          preview?.toggleAttachment(slotName, item).then(() => saveCharacterState());
        }
      });
      grid.appendChild(chip);
    });
  }

  buildAttachGrid('set-hair-grid', HAIR, 'hair');
  buildAttachGrid('set-headgear-grid', HEADGEAR, 'headgear');
  buildAttachGrid('set-earring-grid', EARRINGS, 'earrings');
  buildMultiAttachGrid('set-piercing-grid', PIERCINGS, 'piercings');
  buildAttachGrid('set-mask-grid', MASKS, 'mask');

  // ── Save character state (debounced) ──
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  function saveCharacterState(): void {
    if (!preview || !getJwt()) return;
    if (saveTimeout) clearTimeout(saveTimeout);
    saveTimeout = setTimeout(async () => {
      const charFile = preview!.getCurrentCharFile();
      const ch = CHARACTERS.find(c => c.file === charFile);
      if (!ch) return;

      // Collect active attachment chips
      const attachments: Record<string, string> = {};
      for (const [slotName, gridId] of [
        ['hair', 'set-hair-grid'], ['headgear', 'set-headgear-grid'],
        ['earrings', 'set-earring-grid'], ['mask', 'set-mask-grid'],
      ] as const) {
        const active = settingsCol.querySelector(`#${gridId} .set-attach-chip.active`) as HTMLElement | null;
        if (active && active.textContent !== 'None') attachments[slotName] = active.textContent!;
      }
      // Multi-select piercings
      const piercingChips = settingsCol.querySelectorAll('#set-piercing-grid .set-attach-chip.active');
      const piercings = Array.from(piercingChips).map(c => c.textContent).filter(t => t !== 'None');
      if (piercings.length) attachments['piercings'] = piercings.join(',');

      // Active skin tone
      const activeMst = settingsCol.querySelector('.set-mst-btn.active') as HTMLElement | null;
      const skinTone = activeMst?.dataset.color ?? '';

      // Active pose
      const activePose = settingsCol.querySelector('#set-pose-t.active, #set-pose-a.active, #set-pose-i.active') as HTMLElement | null;
      const pose = activePose?.id.replace('set-pose-', '') ?? 't';

      // Render portrait
      let portraitData = '';
      try {
        portraitData = await preview!.renderPortrait();
      } catch { /* portrait optional */ }

      patchPreferences({
        characterId: ch.id,
        skinTone,
        pose,
        attachments,
        portraitData,
      }).catch(() => { /* silent save failure */ });
    }, 600);
  }

  // ── Auto-load first character (or saved) ──
  let initialCharLoaded = false;
  function loadSavedCharacter(prefs: Preferences): void {
    if (!preview || initialCharLoaded) return;
    initialCharLoaded = true;

    const savedCh = prefs.characterId
      ? CHARACTERS.find(c => c.id === prefs.characterId)
      : null;
    const ch = savedCh ?? CHARACTERS[0];

    // Activate the correct character button
    const idx = CHARACTERS.indexOf(ch);
    charGrid.querySelectorAll('.set-char-btn').forEach((b, i) => {
      b.classList.toggle('active', i === idx);
    });

    preview.selectCharacter(ch).then(async () => {
      // Restore skin tone
      if (prefs.skinTone) {
        const tone = MST_TONES.find(t => t.color === prefs.skinTone);
        if (tone) {
          mstGrid.querySelectorAll('.set-mst-btn').forEach(b => b.classList.remove('active'));
          const mstBtn = mstGrid.querySelector(`.set-mst-btn[data-color="${prefs.skinTone}"]`) as HTMLElement | null;
          mstBtn?.classList.add('active');
          preview!.applySkinTone(tone.color);
        }
      }
      // Restore pose
      if (prefs.pose && prefs.pose !== 't') {
        settingsCol.querySelectorAll('#set-pose-t,#set-pose-a,#set-pose-i').forEach(b => b.classList.remove('active'));
        const poseBtn = settingsCol.querySelector(`#set-pose-${prefs.pose}`) as HTMLElement | null;
        poseBtn?.classList.add('active');
        preview!.applyPose(prefs.pose);
      }

      // Restore attachments
      if (prefs.attachments) {
        const singleSlots: [string, string, AttachmentDef[]][] = [
          ['hair', 'set-hair-grid', HAIR],
          ['headgear', 'set-headgear-grid', HEADGEAR],
          ['earrings', 'set-earring-grid', EARRINGS],
          ['mask', 'set-mask-grid', MASKS],
        ];
        for (const [slot, gridId, defs] of singleSlots) {
          const savedName = prefs.attachments[slot];
          if (!savedName) continue;
          const item = defs.find(d => d.name === savedName);
          if (!item) continue;
          const grid = settingsCol.querySelector(`#${gridId}`)!;
          grid.querySelectorAll('.set-attach-chip').forEach(c => {
            c.classList.toggle('active', c.textContent === savedName);
          });
          await preview!.equipAttachment(slot, item);
        }

        // Restore piercings (multi-select, comma-separated)
        const savedPiercings = prefs.attachments['piercings'];
        if (savedPiercings) {
          const names = savedPiercings.split(',');
          const grid = settingsCol.querySelector('#set-piercing-grid')!;
          grid.querySelectorAll('.set-attach-chip').forEach(c => {
            const isNone = c.textContent === 'None';
            c.classList.toggle('active', !isNone && names.includes(c.textContent!));
          });
          for (const name of names) {
            const item = PIERCINGS.find(d => d.name === name);
            if (item) await preview!.toggleAttachment('piercings', item);
          }
        }
      }

      // Re-render portrait with current camera settings and save it
      // (ensures portrait reflects latest zoom/angle changes)
      saveCharacterState();
    });
  }

  if (preview && !getJwt()) {
    // No auth — just load default
    preview.selectCharacter(CHARACTERS[0]);
  }

  // ── Status flash helper ──
  function flashStatus(el: HTMLElement, text: string, color: string): void {
    el.textContent = text;
    el.style.color = color;
    el.style.opacity = '1';
    setTimeout(() => { el.style.opacity = '0'; }, 2000);
  }

  const accentStatus = settingsCol.querySelector('#set-accent-status') as HTMLElement;
  const notifStatus  = settingsCol.querySelector('#set-notif-status')  as HTMLElement;

  // ── Load preferences from server ──
  function applyPrefs(prefs: Preferences): void {
    const matchBtn = settingsCol.querySelector(`.set-accent-btn[data-color="${prefs.accentColor}"]`) as HTMLElement | null;
    if (matchBtn) {
      settingsCol.querySelectorAll('.set-accent-btn').forEach(b => b.classList.remove('active'));
      matchBtn.classList.add('active');
    }
    document.documentElement.style.setProperty('--cyan', prefs.accentColor);
    document.documentElement.style.setProperty('--cyan-dim', prefs.accentColor + 'aa');

    filters.crit = prefs.notifCrit;
    filters.warn = prefs.notifWarn;
    filters.info = prefs.notifInfo;
    for (const level of ['crit', 'warn', 'info'] as const) {
      const toggle = settingsCol.querySelector(`#set-notif-${level}`) as HTMLElement;
      if (toggle) {
        toggle.classList.toggle('on', filters[level]);
        toggle.setAttribute('aria-checked', String(filters[level]));
      }
    }
  }

  if (getJwt()) {
    getPreferences()
      .then(prefs => {
        applyPrefs(prefs);
        loadSavedCharacter(prefs);
      })
      .catch(() => {
        // Fallback: load default character
        if (preview && !initialCharLoaded) {
          initialCharLoaded = true;
          preview.selectCharacter(CHARACTERS[0]);
        }
      });
  }

  // ── Font size switching ──
  const fontSizeSelect = settingsCol.querySelector('#set-font-size') as HTMLSelectElement;
  const savedFontSize = localStorage.getItem('corpllm_font_size') ?? '14';
  fontSizeSelect.value = savedFontSize;
  document.documentElement.style.fontSize = savedFontSize + 'px';

  fontSizeSelect.addEventListener('change', () => {
    const size = fontSizeSelect.value;
    document.documentElement.style.fontSize = size + 'px';
    localStorage.setItem('corpllm_font_size', size);
    flashStatus(accentStatus, t('settings.saved'), 'var(--green)');
  });

  // ── Accent color switching (with server sync) ──
  const accentRow = settingsCol.querySelector('#set-accent-row')!;
  accentRow.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.set-accent-btn') as HTMLElement | null;
    if (!btn) return;
    accentRow.querySelectorAll('.set-accent-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    const color = btn.dataset.color!;
    document.documentElement.style.setProperty('--cyan', color);
    document.documentElement.style.setProperty('--cyan-dim', color + 'aa');

    if (getJwt()) {
      patchPreferences({ accentColor: color })
        .then(() => flashStatus(accentStatus, t('settings.saved'), 'var(--green)'))
        .catch(() => flashStatus(accentStatus, t('settings.error'), 'var(--red)'));
    }
  });

  // ── Notification filter toggles (with server sync) ──
  for (const level of ['crit', 'warn', 'info'] as const) {
    const toggle = settingsCol.querySelector(`#set-notif-${level}`) as HTMLElement;
    toggle.addEventListener('click', () => {
      filters[level] = !filters[level];
      toggle.classList.toggle('on', filters[level]);
      toggle.setAttribute('aria-checked', String(filters[level]));

      const key = `notif${level[0].toUpperCase()}${level.slice(1)}` as keyof Preferences;
      if (getJwt()) {
        patchPreferences({ [key]: filters[level] } as Partial<Preferences>)
          .then(() => flashStatus(notifStatus, t('settings.saved'), 'var(--green)'))
          .catch(() => flashStatus(notifStatus, t('settings.error'), 'var(--red)'));
      }
    });
  }

  // ── Language switching ──
  const langDe = settingsCol.querySelector('#set-lang-de') as HTMLElement;
  const langEn = settingsCol.querySelector('#set-lang-en') as HTMLElement;
  for (const [btn, lang] of [[langDe, 'de'], [langEn, 'en']] as const) {
    btn.addEventListener('click', () => {
      langDe.classList.toggle('active', lang === 'de');
      langEn.classList.toggle('active', lang === 'en');
      setLang(lang);
      window.location.reload();
    });
  }

  // ── Default corp name (lobby pre-fill) ──
  const defaultCorpInput = settingsCol.querySelector('#set-default-corp') as HTMLInputElement;
  defaultCorpInput.addEventListener('input', () => {
    try { localStorage.setItem('defaultCorpName', defaultCorpInput.value.trim()); } catch { /* ignore */ }
  });

  // ── Admin mode toggle ──
  const adminToggle = settingsCol.querySelector('#set-admin-toggle') as HTMLElement | null;
  if (adminToggle) {
    adminToggle.addEventListener('click', () => {
      const isOn = adminToggle.classList.toggle('on');
      localStorage.setItem('corpllm_admin_mode', isOn ? '1' : '0');
    });
  }

  // ── Logout ──
  settingsCol.querySelector('#set-logout')!.addEventListener('click', () => {
    clearJwt();
    location.reload();
  });

  // ── Cleanup on navigation away ──
  const observer = new MutationObserver(() => {
    if (!root.isConnected) {
      preview?.dispose();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return root;
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
